import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronRight, Shield, Target, Zap, Award,
  ArrowRight, Check, ChevronDown, TrendingUp, Timer
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import LegalFooter from "./legal/LegalFooter";
import PublicNavbar from "./PublicNavbar";

const AthleteHomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    if (user) {
      const isAdminOrCoach = user?.role === "admin" || user?.role === "coach";
      const sub = user?.subscription_tier;
      const hasActive = sub && sub !== "free" && sub !== "none" && sub !== "";
      if (isAdminOrCoach || hasActive) navigate("/dashboard");
    }
  }, [user, navigate]);

  const testimonials = [
    {
      name: "Marcus Chen",
      role: "Collegiate Athlete",
      quote: "Paladin transformed my training. I added 40lbs to my squat in 3 months.",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop"
    },
    {
      name: "Sarah Williams",
      role: "CrossFit Competitor",
      quote: "The AI coaching is like having a world-class trainer in my pocket 24/7.",
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop"
    },
    {
      name: "David Park",
      role: "Professional Basketball",
      quote: "My vertical jump increased by 6 inches. The programming is next level.",
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop"
    }
  ];

  const pillars = [
    { icon: Zap, title: "EXPLOSIVE POWER", desc: "Sprint faster, jump higher, and move with explosive force through data-driven programming." },
    { icon: Target, title: "PRECISION TRAINING", desc: "Every rep, set, and exercise is calculated. AI-driven progression that adapts to your results." },
    { icon: Shield, title: "DURABILITY", desc: "Injury prevention through smart conditioning, pre-hab work, and recovery protocols." },
    { icon: TrendingUp, title: "AUTO-PROGRESSION", desc: "Hit your benchmarks and automatically advance to harder programming. No guesswork." },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#070B14] text-white overflow-x-hidden">
      <PublicNavbar transparent={true} />

      {/* ==================== HERO ==================== */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <video autoPlay muted loop playsInline className="w-full h-full object-cover" poster="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1920&q=80">
            <source src="https://videos.pexels.com/video-files/4761437/4761437-hd_1920_1080_25fps.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-[#070B14]/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#070B14] via-transparent to-[#070B14]/30" />
        </div>

        <div className="relative z-10 w-full max-w-[1800px] mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <p className="text-blue-400 text-sm tracking-[0.3em] uppercase mb-4">For Competitive Athletes</p>
            <h1 className="font-black text-[11vw] md:text-[9vw] lg:text-[8vw] leading-[0.85] tracking-[-0.02em] uppercase">
              <span className="block">TRAIN</span>
              <span className="block text-blue-400/80">LIKE A PRO</span>
            </h1>
            <p className="text-white/90 text-base md:text-lg mt-6 max-w-xl">
              AI-powered programming built for athletes who compete. Get faster, stronger, and more explosive with precision training.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/pricing" className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white px-8 py-4 rounded-full font-bold tracking-[0.1em] uppercase text-sm hover:from-blue-500 hover:to-blue-400 transition-all shadow-lg shadow-blue-500/25" data-testid="athlete-hero-cta">
                Start Training <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/philosophy" className="inline-flex items-center gap-2 border border-white/20 text-white/80 px-8 py-4 rounded-full text-sm tracking-[0.1em] uppercase hover:border-white/40 hover:text-white transition-all">
                Our Philosophy
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-12 left-0 right-0 z-10 flex flex-col items-center">
          <span className="text-xs tracking-[0.2em] uppercase text-white/90 mb-3">Scroll to explore</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>
            <ChevronDown className="w-5 h-5 text-white/90" />
          </motion.div>
        </div>
      </section>

      {/* ==================== WHAT WE BUILD ==================== */}
      <section id="about" className="py-28 px-6 md:px-12 bg-[#070B14]">
        <div className="max-w-[1800px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-blue-400 text-sm tracking-[0.3em] uppercase">01 // The System</span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-6 mb-6 leading-[0.9]">
              BUILT FOR<br /><span className="text-blue-400/80">COMPETITION</span>
            </h2>
            <p className="text-white/80 text-base leading-relaxed max-w-2xl mb-16">
              We don't do generic. Every program is designed around athletic performance — speed, power, strength, and the resilience to keep performing at your peak.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:border-blue-500/30 transition-all duration-500" data-testid={`athlete-pillar-${i}`}>
                <p.icon className="w-8 h-8 text-blue-400 mb-6" />
                <h3 className="text-lg font-bold tracking-tight mb-3">{p.title}</h3>
                <p className="text-white/80 text-sm leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Stats */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20 pt-16 border-t border-white/10">
            {[
              { value: "+6\"", label: "Avg. Vertical Gain" },
              { value: "40lbs", label: "Avg. Squat Increase" },
              { value: "98%", label: "Athlete Retention" },
              { value: "24/7", label: "AI Coach Access" }
            ].map((s, i) => (
              <div key={i}>
                <p className="text-4xl md:text-5xl font-black text-blue-400">{s.value}</p>
                <p className="text-sm text-white/70 uppercase tracking-[0.15em] mt-2">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section id="programs" className="py-28 px-6 md:px-12 bg-gradient-to-b from-[#070B14] to-[#0a1020]">
        <div className="max-w-[1800px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16">
            <span className="text-blue-400 text-sm tracking-[0.3em] uppercase">02 // How It Works</span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-6 leading-[0.9]">
              YOUR PATH TO<br /><span className="text-blue-400/80">ELITE</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Get Your Program", desc: "Choose your tier and get access to structured, periodized training built for your sport and goals." },
              { step: "02", title: "Train & Track", desc: "Log every rep with our guided workout mode. AI calculates your weights, tracks your maxes, and adjusts in real-time." },
              { step: "03", title: "Level Up", desc: "Hit your benchmarks and the system auto-progresses you to harder exercises and heavier loads. No plateau." },
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                className="relative pl-8 border-l-2 border-blue-500/30">
                <span className="text-5xl font-black text-blue-500/30">{item.step}</span>
                <h3 className="text-xl font-bold mt-2 mb-3">{item.title}</h3>
                <p className="text-white/80 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== TESTIMONIALS ==================== */}
      <section id="results" className="py-28 px-6 md:px-12 bg-[#0a1020]">
        <div className="max-w-[1800px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16">
            <span className="text-blue-400 text-sm tracking-[0.3em] uppercase">03 // Results</span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-6 leading-[0.9]">
              ATHLETE<br /><span className="text-blue-400/80">RESULTS</span>
            </h2>
          </motion.div>

          <div className="max-w-4xl">
            <AnimatePresence mode="wait">
              <motion.div key={activeTestimonial} initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} transition={{ duration: 0.5 }} className="border-l-2 border-blue-500/50 pl-8 md:pl-12">
                <blockquote className="text-2xl md:text-3xl lg:text-4xl font-bold leading-tight mb-8">
                  "{testimonials[activeTestimonial].quote}"
                </blockquote>
                <div className="flex items-center gap-4">
                  <img src={testimonials[activeTestimonial].avatar} alt={testimonials[activeTestimonial].name} className="w-14 h-14 object-cover rounded-full" />
                  <div>
                    <p className="font-bold">{testimonials[activeTestimonial].name}</p>
                    <p className="text-white/80 text-sm">{testimonials[activeTestimonial].role}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
            <div className="flex gap-3 mt-12">
              {testimonials.map((_, i) => (
                <button key={i} onClick={() => setActiveTestimonial(i)} className={`h-1 transition-all ${i === activeTestimonial ? "bg-blue-500 w-12" : "bg-white/20 w-8 hover:bg-white/40"}`} />
              ))}
            </div>
          </div>

          {/* Results Grid */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20 pt-16 border-t border-white/10">
            {[
              { sport: "Basketball", improvement: "+6\" Vertical" },
              { sport: "Football", improvement: "+40lbs Squat" },
              { sport: "Soccer", improvement: "2x Speed" },
              { sport: "MMA", improvement: "+15lbs Lean" }
            ].map((r, i) => (
              <div key={i} className="text-center p-6 bg-white/5 rounded-xl border border-white/10">
                <p className="text-3xl md:text-4xl font-black text-blue-400 mb-2">{r.improvement}</p>
                <p className="text-white/70 text-sm uppercase tracking-[0.15em]">{r.sport}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ==================== CTA ==================== */}
      <section className="py-28 px-6 md:px-12 bg-[#070B14] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/15 via-transparent to-transparent" />
        <div className="max-w-[1800px] mx-auto relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center">
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight mb-6 leading-[0.9]">READY TO<br />COMPETE?</h2>
            <p className="text-white/90 text-base mb-10 max-w-xl mx-auto">
              Join athletes training with AI-powered precision. Your next level starts now.
            </p>
            <Link to="/pricing" className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white px-10 py-5 rounded-full font-bold tracking-[0.1em] uppercase text-sm hover:from-blue-500 hover:to-blue-400 transition-all shadow-2xl shadow-blue-500/30" data-testid="athlete-cta-bottom">
              Start Training Today <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
};

export default AthleteHomePage;
